import api from './api';

// ── Dashboard ────────────────────────────────────
export const getDashboardStats = () =>
  api.get('/admin/dashboard/stats').then(r => r.data.data);

export const getAdminDashboardStats = getDashboardStats;

export const getRecentAppointments = (limit = 8) =>
  api.get('/admin/appointments/recent', { params: { limit } }).then(r => r.data.data);

// ── Users ──────────────────────────────────────
export const getAdminUsers = (params) =>
  api.get('/admin/users', { params }).then(r => r.data);

export const getAdminUserDetail = (id) =>
  api.get(`/admin/users/${id}`).then(r => r.data.data);

/**
 * PATCH /api/admin/users/:id/status — alterna activo/inactivo (sin cuerpo).
 * Llamaba a /toggle-status, que no existe: el botón daba 404.
 */
export const toggleUserStatus = (id) =>
  api.patch(`/admin/users/${id}/status`).then(r => r.data.data);

export const toggleAdminUserStatus = toggleUserStatus;

/**
 * DELETE /api/admin/users/:id — soft delete con anonimización.
 * Si el usuario tiene citas activas el backend responde 409 con
 * `data.activeAppointments`; el error se propaga para que la UI las muestre.
 */
export const deleteAdminUser = (id) =>
  api.delete(`/admin/users/${id}`).then(r => r.data);

// ── Doctors ──────────────────────────────────────
export const getAdminDoctors = (params) =>
  api.get('/admin/doctors', { params }).then(r => r.data);

export const getIncompleteDoctors = (params) =>
  api.get('/admin/doctors/incomplete', { params }).then(r => r.data);

export const verifyDoctor = (id, isVerified) =>
  api.patch(`/admin/doctors/${id}/verify`, { isVerified }).then(r => r.data);

/**
 * Crear médico nuevo  → POST /api/admin/doctors
 * Completar perfil    → POST /api/admin/doctors/complete-profile
 */
export const createAdminDoctor = (data) => {
  if (data.prefillUserId) {
    const { prefillUserId, firstName, lastName, email, password, ...professional } = data;
    return api
      .post('/admin/doctors/complete-profile', { userId: prefillUserId, ...professional })
      .then(r => r.data.data);
  }
  return api.post('/admin/doctors', data).then(r => r.data.data);
};

/** PUT /api/admin/doctors/:id — editar perfil completo */
export const updateAdminDoctor = (id, data) =>
  api.put(`/admin/doctors/${id}`, data).then(r => r.data.data);

/** DELETE /api/admin/doctors/:id — soft delete */
export const deleteAdminDoctor = (id) =>
  api.delete(`/admin/doctors/${id}`).then(r => r.data);

// ── Firma del médico ─────────────────────────────
// Se estampa en los PDFs de historia clínica junto al nombre y el registro médico.

/** PUT /api/admin/doctors/:id/signature — dataUrl de un PNG/JPEG (máx. 300 KB) */
export const uploadDoctorSignature = (id, dataUrl) =>
  api.put(`/admin/doctors/${id}/signature`, { dataUrl }).then(r => r.data.data);

/**
 * GET /api/admin/doctors/:id/signature — devuelve la imagen.
 * Se pide como blob y se convierte a object URL para mostrarla en el panel
 * (la ruta exige el token JWT, así que no sirve como src directo de un <img>).
 * Devuelve null si el médico no tiene firma registrada.
 */
export const getDoctorSignatureUrl = (id) =>
  api.get(`/admin/doctors/${id}/signature`, { responseType: 'blob' })
    .then(r => URL.createObjectURL(r.data))
    .catch(err => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

/** DELETE /api/admin/doctors/:id/signature */
export const deleteDoctorSignature = (id) =>
  api.delete(`/admin/doctors/${id}/signature`).then(r => r.data);

// ── Appointments ───────────────────────────────────
export const getAdminAppointments = (params) =>
  api.get('/admin/appointments', { params }).then(r => r.data);

/** PATCH /api/admin/appointments/:id/payment-status — marcar pago manual (paid/failed) */
export const updateAppointmentPaymentStatus = (id, status) =>
  api.patch(`/admin/appointments/${id}/payment-status`, { status }).then(r => r.data.data);

/**
 * DELETE /api/admin/appointments/:id/permanent — borra una cita no atendida
 * (programada o confirmada, sin historia clínica). Un pago completado queda
 * como reembolsado; el reembolso en Wompi o banco se hace por fuera.
 */
export const deleteAppointmentPermanently = (id, reason) =>
  api.delete(`/admin/appointments/${id}/permanent`, { data: { reason } }).then(r => r.data);

/**
 * PATCH /api/admin/appointments/:id/doctor — reasigna la cita a otro médico,
 * en cualquier estado. El motivo queda en la auditoría.
 */
export const changeAppointmentDoctor = (id, doctorId, reason) =>
  api.patch(`/admin/appointments/${id}/doctor`, { doctorId, reason }).then(r => r.data);

// ── Revenue ─────────────────────────────────────
export const getRevenueReport = (months) =>
  api.get('/admin/revenue', { params: { months } }).then(r => r.data.data);

export const getAdminRevenue = getRevenueReport;

// ── Pagos a médicos (payouts) ────────────────────
export const getDoctorsBalances = () =>
  api.get('/admin/doctors/balances').then(r => r.data.data);

export const getDoctorBalanceDetail = (doctorId) =>
  api.get(`/admin/doctors/${doctorId}/balance-detail`).then(r => r.data.data);

export const markDoctorAppointmentPaid = (appointmentId, doctorPayoutMethod = 'Nequi') =>
  api.post(`/admin/appointments/${appointmentId}/mark-doctor-paid`, { doctorPayoutMethod }).then(r => r.data.data);

export const markAllDoctorAppointmentsPaid = (doctorId, doctorPayoutMethod = 'Nequi') =>
  api.post(`/admin/doctors/${doctorId}/mark-all-paid`, { doctorPayoutMethod }).then(r => r.data.data);

// ── Gastos y balance mensual ─────────────────────
export const getExpenses = (month) =>
  api.get('/admin/expenses', { params: month ? { month } : {} }).then(r => r.data.data);

export const createExpense = (data) =>
  api.post('/admin/expenses', data).then(r => r.data.data);

export const updateExpense = (id, data) =>
  api.put(`/admin/expenses/${id}`, data).then(r => r.data.data);

export const deleteExpense = (id) =>
  api.delete(`/admin/expenses/${id}`).then(r => r.data);

export const getFinanceHistory = (months = 12, until) =>
  api.get('/admin/finance/history', { params: until ? { months, until } : { months } }).then(r => r.data.data);

// ── Interruptor del registro público de médicos ──────────────────────────────
export const getDoctorRegistrationSetting = () =>
  api.get('/admin/settings/doctor-registration').then(r => r.data.data.open);

export const setDoctorRegistrationSetting = (open) =>
  api.put('/admin/settings/doctor-registration', { open }).then(r => r.data.data.open);
